import {Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {Address} from '../../../../models/address.model';
import {ContactDetails} from '../../../../interfaces/contact-details';
import {isEmptyArray} from '../../../../shared/utils';
import { ConfigService } from '../../../../services/config.service';
import { AddressBookFeatures } from '../../../../interfaces/address-book-features';

const ADDITIONAL_CONTACTS_MAX_LIMIT = 10;
const EMAIL_PATTERN = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const emptyMainContactDetails  = {
  contact: '',
  email: '',
  main: true,
};

const emptyContacts = [
  emptyMainContactDetails
];



function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const email = control.value;

    if (email) {
      if (!EMAIL_PATTERN.test(control.value)) {
        return {pattern: 'invalid'}
      }

      return null
    }

    return null;
  };
}

function maxLengthAndRequiredValidator(maxLength: number, isRequired: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const trimmedValue = control.value?.trim() || '';

    if (isRequired === true && !trimmedValue) {
      return { required: true };
    }
    if (maxLength > 0 && trimmedValue.length > maxLength) {
      return { maxLengthTrimmed: { requiredLength: maxLength, actualLength: trimmedValue.length } };
    }

    return null;
  };
}


@Component({
    selector: 'app-edit-contact-details',
    templateUrl: './edit-contact-details.component.html',
    styleUrls: ['./edit-contact-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class EditContactDetailsComponent implements OnInit {
  numberOfAdditionalFields = ADDITIONAL_CONTACTS_MAX_LIMIT;


  contactForm = new UntypedFormGroup({
    contact_details: new FormArray([])
  });

  contact_details: FormArray;

  /**
   * FormArray.controls is typed AbstractControl[], which has no `.controls`, so
   * every contact_detail.controls.* access in the template failed strictTemplates.
   * The entries are always FormGroups (built in buildContactDetailsForm), so this
   * narrows them once for the *ngFor. It returns the SAME array instance that
   * FormArray holds - a cast, not a copy - so *ngFor identity stays stable.
   */
  get contactDetailGroups(): FormGroup[] {
    return this.contact_details.controls as FormGroup[];
  }

  @Input() address: Address;

  @Output() onChange = new EventEmitter<{isValid: boolean, contactDetails: ContactDetails[]}>();

  constructor(private configService: ConfigService) { }

  ngOnInit(): void {
    if (isEmptyArray(this.address.contact_details)) {
      this.address.contact_details = emptyContacts;
    } else {
      const hasMainContacts = this.address.contact_details.find( c => c.main === true);
      if (!hasMainContacts) {
        // Promote first contact to main instead of inserting an empty main row
        this.address.contact_details[0] = { ...this.address.contact_details[0], main: true };
      }
    }

    this.contact_details = new FormArray([]);

    if (this.address && this.address.contact_details) {
      this.address.contact_details.forEach((item) => {
        const isMain = item.main === true;
     
        const contactValidators = maxLengthAndRequiredValidator(
          250,
          isMain
            ? this.addressBookFeatures.mainContactNameRequired
            : this.showAdditionalContacts && this.addressBookFeatures.additionalContactNameRequired
        );

        const emailValidators = [
          emailValidator(),
          maxLengthAndRequiredValidator(
            250,
            isMain
              ? this.addressBookFeatures.mainContactEmailRequired
              : this.showAdditionalContacts && this.addressBookFeatures.additionalContactEmailRequired
          )
        ];

        this.contact_details.push(
          new FormGroup({
            contact: new UntypedFormControl(item.contact, [contactValidators]),
            email: new UntypedFormControl(item.email, emailValidators),
            main: new UntypedFormControl(item.main)
          })
        );
      });

      // trigger change after initial values are set
      this.handleContactDetailsChange();
    }
  }



  onAddContactDetail(): void {
    if (this.contact_details.controls.length < ADDITIONAL_CONTACTS_MAX_LIMIT) {
      this.contact_details.push(
        new FormGroup({
          contact: new UntypedFormControl('', [
            maxLengthAndRequiredValidator(250, this.showAdditionalContacts && this.addressBookFeatures.additionalContactNameRequired)
          ]),
          email: new UntypedFormControl('', [
            emailValidator(),
            maxLengthAndRequiredValidator(250, this.showAdditionalContacts && this.addressBookFeatures.additionalContactEmailRequired)
          ]),
          main: new UntypedFormControl(false)
        })
      )
    }
  }


  onRemoveContactDetail(indexToRemove: number) {
    const contact: FormGroup  =  this.contact_details.controls[indexToRemove] as FormGroup;
    const isMain   =  contact.controls['main'].value === true;

    if (isMain) {
      // for main contract  just reset  input fields,do not remove them
      contact.controls['contact'].setValue('');
      contact.controls['email'].setValue('');
    } else {
      this.contact_details.removeAt(indexToRemove);
    }

    this.handleContactDetailsChange();

    return false;
  }

  validationClassName(control: AbstractControl): string {
    if (control.touched || control.dirty) {
      return control.invalid ? 'has-danger' : '';
    }
    return '';
  }

  handleContactDetailsChange() {
    // check if all form controls are valid
    const isValid = this.contact_details.controls.every(c => c.valid);
    // Extract and Trim Input Values
    let contactDetails: ContactDetails[] = this.contact_details.controls.map((item) => ({
      contact: item.get('contact')?.value?.trim() ?? '',
      email: item.get('email')?.value?.trim() ?? '',
      main: item.get('main')?.value ?? false
    }));

    // Filter contacts:
    contactDetails = contactDetails.filter(c =>
      c.contact !== '' || c.email !== '' // Keep rows where at least contact or email exists
    );

    // Validate: Only email without contact is NOT allowed when contact name is required
    const hasEmailWithoutRequiredContact = contactDetails.some(c => {
      if (c.contact !== '' || c.email === '') {
        return false;
      }
      // Name is empty and email is filled — only invalid if name is required
      return c.main
        ? this.addressBookFeatures.mainContactNameRequired
        : this.showAdditionalContacts && this.addressBookFeatures.additionalContactNameRequired;
    });

    if (!isValid || hasEmailWithoutRequiredContact) {
      this.onChange.emit({ isValid: false, contactDetails: [] });
      return;
    }

    this.onChange.emit({isValid: true, contactDetails});
  }

  onInputChange() {
    // Perform actions based on the new value
    this.handleContactDetailsChange();
  }

  markAllAsTouched(): void {
    (this.contact_details.controls as UntypedFormGroup[]).forEach(group => {
      Object.values(group.controls).forEach(control => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });
    });

    this.handleContactDetailsChange();
  }

  get addressBookFeatures(): AddressBookFeatures {
    return this.configService.addressBookFeatures;
  }

  get showMainContacts(): boolean {
    const {showMainContactEmail, showMainContactName} = this.configService.addressBookFeatures;
    return showMainContactEmail && showMainContactName;
  }

  get showAdditionalContacts(): boolean {
    const {showAdditionalContactEmail, showAdditionalContactName} = this.configService.addressBookFeatures;
    return showAdditionalContactEmail && showAdditionalContactName;
  }
}
